import React, { useState } from 'react';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import { formatDate } from '../../utils/dateUtils';
import useManageOrder from '../order/useManageOrder';

// Search an order and pick it from the HITS — never "the first match". The
// backend matches smileNameInput and the customer's contact case-insensitively,
// so a name search returns many orders; taking rows[0] silently linked whichever
// came first. Renders no <form> of its own: it lives inside one.
const OrderPicker = ({
  customerId = null,
  onPick,
  label = 'ค้นหาออเดอร์ (ชื่อลูกค้า / ลิงก์ / เลขพัสดุ)',
  pickedIds = [],
}) => {
  const [text, setText] = useState('');
  const [hits, setHits] = useState(null);
  const [searching, setSearching] = useState(false);
  const { getLatestOrder } = useManageOrder();

  const search = async () => {
    if (!text.trim()) return;
    setSearching(true);
    const rows = await getLatestOrder({ searchText: text.trim() });
    setSearching(false);
    setHits(Array.isArray(rows) ? rows : []);
  };

  // An order of another customer can never belong to this case (the backend
  // refuses it too) — hide those rather than let them be clicked.
  const mine = customerId ? (hits || []).filter((o) => o.customerId === customerId) : (hits || []);
  const hidden = (hits || []).length - mine.length;

  return (
    <div>
      <div className="flex gap-1">
        <TextField
          sx={{ flexGrow: 1, bgcolor: 'white' }} size="small" label={label}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); search(); } }}
        />
        <IconButton type="button" onClick={search} style={{ backgroundColor: 'green', borderRadius: '50%' }}>
          <SearchIcon style={{ color: 'white' }} />
        </IconButton>
      </div>

      {searching && <p className="text-sm text-gray-500 mt-1">กำลังค้นหา...</p>}

      {hits && !searching && (
        mine.length === 0
          ? <p className="text-sm text-gray-500 mt-1">ไม่พบออเดอร์{hidden > 0 ? ` (ซ่อน ${hidden} รายการที่ไม่ใช่ของลูกค้าคนนี้)` : ''}</p>
          : (
            <div className="mt-1 border border-blue-300 rounded bg-white max-h-56 overflow-y-auto">
              {/* the hits are a choice to make — say so, the form's button comes after */}
              <p className="text-xs font-semibold text-blue-800 bg-blue-50 px-2 py-1">👇 กดเลือกออเดอร์ที่ลูกค้าตาม</p>
              {mine.map((o) => {
                const already = pickedIds.includes(o.id);
                return (
                  <button
                    key={o.id} type="button" disabled={already}
                    onClick={() => { onPick(o); setHits(null); setText(''); }}
                    className={`w-full text-left flex items-center gap-2 px-2 py-1.5 border-b border-gray-100 last:border-0 ${
                      already ? 'opacity-40 cursor-default' : 'hover:bg-blue-50'
                    }`}
                  >
                    {o.imageUrl && o.imageUrl !== '-' && <img src={o.imageUrl} alt="" className="w-8 h-8 object-cover rounded shrink-0" />}
                    <span className="flex-1 min-w-0">
                      {/* a send-only (ฝากส่ง) order has no link and no price — it is its tracking number */}
                      {o.isSendOnly
                        ? <span className="block text-sm font-medium truncate">📦 ฝากส่ง · {o.trackingJp && o.trackingJp !== 'ไม่มีเลข' ? o.trackingJp : 'ไม่มีเลขพัสดุ'}</span>
                        : <span className="block text-sm truncate">{o.url}</span>}
                      <span className="block text-xs text-gray-500 truncate">
                        {o.isSendOnly ? '' : `${o.amount} เยน · `}{o.orderDate ? formatDate(o.orderDate) : '—'}
                        {!o.isSendOnly && (o.trackingJp && o.trackingJp !== 'ไม่มีเลข' ? ` · ${o.trackingJp}` : ' · ไม่มีเลขพัสดุ')}
                        {o.smileNameInput ? ` · ${o.smileNameInput}` : ''}
                      </span>
                    </span>
                    {already
                      ? <span className="text-xs text-gray-400 shrink-0">เพิ่มแล้ว</span>
                      : <span className="text-xs font-semibold text-white bg-blue-600 rounded-full px-2 py-0.5 shrink-0">+ เลือก</span>}
                  </button>
                );
              })}
              {hidden > 0 && (
                <p className="text-xs text-gray-400 px-2 py-1">ซ่อน {hidden} รายการที่ไม่ใช่ของลูกค้าคนนี้</p>
              )}
            </div>
          )
      )}
    </div>
  );
};

export default OrderPicker;
